import type { GroupBookingData, Label, Occupancy, Participant } from '@/scopes/account/group-responsible/domain/types';
import type {
  GroupBookingAPIResponse,
  ParticipantPaymentInfoAPIResponse,
} from '@/scopes/account/group-responsible/infrastructure/types';
import type { Locale } from '@/intl/types';

export function groupBookingMapper(apiResponse: GroupBookingAPIResponse, locale?: Locale): GroupBookingData {
  const ONE_DAY_IN_MS = 1000 * 60 * 60 * 24;

  const getParticipantStatus = (paymentInfo: ParticipantPaymentInfoAPIResponse): Label => {
    if (paymentInfo.totalPaid === 0) {
      return {
        text: 'groups_responsible_not_paid',
        type: 'error',
      };
    }
    if (paymentInfo.totalPaid === paymentInfo.totalToBePaid) {
      return {
        text: 'groups_responsible_paid',
        type: 'success',
      };
    }
    return {
      text: 'groups_responsible_partial_paid',
      type: 'warning',
    };
  };

  const getLabels = (): Label[] => {
    const journeyLabel = getJourneyLabel();

    return [journeyLabel].filter((label) => label !== undefined) as Label[];
  };

  const getJourneyLabel = (): Label | undefined => {
    if (!apiResponse?.data?.startDate || !apiResponse?.data?.endDate) return undefined;

    const today = new Date(new Date().setHours(0, 0, 0, 0));
    const start = new Date(apiResponse.data.startDate);
    const end = new Date(apiResponse.data.endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return undefined;

    if (start > today) {
      const daysLeft = Math.floor((start.getTime() - today.getTime()) / ONE_DAY_IN_MS);
      if (daysLeft === 1) {
        return {
          text: 'group_responsible_label_one_day_left',
          type: 'brand',
        };
      }
      return {
        text: 'groups_responsible_label_days_left',
        type: 'brand',
        placeholders: {
          days: `${daysLeft}`,
        },
      };
    }

    if (end >= today) {
      return {
        text: 'groups_responsible_label_during_trip',
        type: 'info',
      };
    }

    return undefined;
  };

  const getParticipantAmountDue = (paymentInfo: ParticipantPaymentInfoAPIResponse): number =>
    paymentInfo.totalToBePaid - paymentInfo.totalPaid;

  const getParticipantName = (firstName: string, lastName: string): string => `${firstName} ${lastName}`;

  return {
    group: {
      name: apiResponse.data.groupName ?? '',
      location: '', //TODO: Retrieve from API MS-5143
      period:
        apiResponse.data.startDate && apiResponse.data.endDate
          ? getPeriod(apiResponse.data.startDate, apiResponse.data.endDate, locale)
          : '',
      imgSrc: 'https://www.sunweb.nl/-/media/07_sunweb-int/my-accounts/sunweb-my-account-header.ash',
    },
    labels: getLabels(),

    participants: apiResponse.data.childBookings
      .flatMap((item) => {
        const serviceMap = new Map(item.services.map((service) => [service.groupCode, service]));
        const hasService = (groupCode: string, participantId: number): boolean => {
          const service = serviceMap.get(groupCode);
          return service ? service.participantIds.includes(participantId) : false;
        };

        return item.participants.map((participant) => {
          const skiPassName = hasService('SKIPAS', participant.id) ? serviceMap.get('SKIPAS')?.name || '' : '';
          const hasSkiMaterial = hasService('SKIHUUR', participant.id);
          const hasTotallySweater = hasService('SWEATER', participant.id);

          return {
            id: participant.id.toString(),
            bookingNumber: item.bookingNumber,
            name: getParticipantName(participant.firstName, participant.lastName),
            email: participant.email,
            status: getParticipantStatus(participant.paymentInfo),
            room: participant.used ? 'groups_responsible_room_assigned' : 'groups_responsible_room_not_assigned',
            assigned: participant.used,
            amountDue: getParticipantAmountDue(participant.paymentInfo),
            telephoneNumber: participant.telephoneNumber,
            mobileNumber: participant.mobileNumber,
            skiPass: skiPassName || '',
            hasSkiMaterial,
            hasTotallySweater,
          };
        });
      })
      .reduce((acc: Record<string, Participant>, participant) => {
        acc[participant.id.toString()] = participant;
        return acc;
      }, {}),

    rooms: apiResponse.data.childBookings
      .map((room) => {
        const accommodationService = room.services.find((service) => service.typeOfService === 'Accommodation');

        const occupancy: Occupancy = {
          occupied: room.assignedParticipants,
          max: room.maxParticipants,
        };

        const occupancyText = `${occupancy.occupied}/${occupancy.max}`;

        return {
          id: room.bookingNumber.toString(),
          name: room.bookingNumber.toString(),
          type: '', //TODO:waiting for the API
          occupancy,
          occupancyText,
          participants: room.participants.map((participant) =>
            getParticipantName(participant.firstName, participant.lastName)
          ),
          accommodationName: accommodationService?.name || '',
          participantsId: room.participants.map((participant) => participant.id),
        };
      })
      .reduce((acc: Record<string, typeof room>, room) => {
        acc[room.id.toString()] = room;
        return acc;
      }, {}),

    buses: apiResponse.data.childBookings
      .flatMap((item) =>
        item.services
          .filter((service) => service.typeOfService === 'Bus')
          .map((service) => {
            const validDirections = new Set(['Inbound', 'Outbound']);
            const direction =
              typeof service.direction === 'string' && validDirections.has(service.direction)
                ? service.direction
                : undefined;

            return {
              id: service.id.toString(),
              arrivalLocation: service.arrivalLocation || '',
              departureLocation: service.departureLocation || '',
              direction,
              busNumber: service.busNumber || '',
              participantsId: service.participantIds,
            };
          })
      )
      .reduce((acc: Record<string, typeof service>, service) => {
        acc[service.id.toString()] = service;
        return acc;
      }, {}),
  };
}

export function getPeriod(startDate: string, endDate: string, locale: Locale = 'nl-NL'): string {
  const startDateObj = new Date(startDate);
  const endDateObj = new Date(endDate);

  if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
    return '';
  }

  const startDay = startDateObj.toLocaleDateString(locale, { day: 'numeric' });
  const endDay = endDateObj.toLocaleDateString(locale, { day: 'numeric' });
  const month = endDateObj.toLocaleDateString(locale, { month: 'long' });
  const year = endDateObj.toLocaleDateString(locale, { year: 'numeric' });

  return `${startDay} - ${endDay} ${month} ${year}`;
}
